import React from 'react';
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { PriceSlider } from "@/components/ui/price-slider";
import { ArtistFacet } from './ArtistFacet';
import { AlbumFacet } from './AlbumFacet';
import { SoftwareType, HolidayType } from '@/types/sequence';
import PropsUsedFacet from './PropsUsedFacet';

interface ArtistCount {
  name: string;
  count: number;
}

interface AlbumCount {
  name: string;
  count: number;
}

interface SequenceFiltersSidebarProps {
  selectedSoftware: SoftwareType[];
  selectedHolidays: HolidayType[];
  selectedGenres: string[];
  selectedArtists: string[];
  selectedAlbums: string[];
  priceRange: [number, number];
  allGenres: string[];
  artists: ArtistCount[];
  albums: AlbumCount[];
  onSoftwareChange: (software: SoftwareType) => void;
  onHolidayChange: (holiday: HolidayType) => void;
  onGenreChange: (genre: string) => void;
  onArtistChange: (artist: string) => void;
  onAlbumChange: (album: string) => void;
  onPriceChange: (value: [number, number]) => void;
  clearFilters: () => void;
  holidayCounts?: { name: string; count: number }[];
}

const SOFTWARE_OPTIONS: SoftwareType[] = ['xLights', 'LOR'];
const HOLIDAY_OPTIONS: HolidayType[] = [
  'Christmas',
  'Halloween',
  'Fourth of July',
  'Easter',
  'Thanksgiving',
  'Hanukkah'
];

const formatHolidayName = (holiday: HolidayType): string => {
  switch (holiday) {
    case 'FourthOfJuly':
      return '4th of July';
    default:
      return holiday;
  }
};

export function SequenceFiltersSidebar({
  selectedSoftware,
  selectedHolidays,
  selectedGenres,
  selectedArtists,
  selectedAlbums,
  priceRange,
  allGenres,
  artists,
  albums,
  onSoftwareChange,
  onHolidayChange,
  onGenreChange,
  onArtistChange,
  onAlbumChange,
  onPriceChange,
  clearFilters,
  holidayCounts = []
}: SequenceFiltersSidebarProps) {
  return (
    <div className="w-64 space-y-6">
      {/* Software Filter */}
      <Card className="p-4">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Label className="text-base font-medium">Software</Label>
          </div>
          <div className="space-y-2">
            {SOFTWARE_OPTIONS.map(software => (
              <div key={software} className="flex items-center space-x-2">
                <Checkbox
                  id={`software-${software}`}
                  checked={selectedSoftware.includes(software)}
                  onCheckedChange={() => onSoftwareChange(software)}
                />
                <Label htmlFor={`software-${software}`} className="text-sm">
                  {software}
                </Label>
              </div>
            ))}
          </div>
        </div>
      </Card>

      {/* Artist Filter */}
      <ArtistFacet
        artists={artists}
        selectedArtists={selectedArtists}
        onArtistChange={onArtistChange}
      />

      {/* Holiday Filter */}
      <Card className="p-4">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Label className="text-base font-medium">Holiday</Label>
          </div>
          <div className="space-y-2">
            {HOLIDAY_OPTIONS.map(holiday => (
              <div key={holiday} className="flex items-center space-x-2">
                <Checkbox
                  id={`holiday-${holiday}`}
                  checked={selectedHolidays.includes(holiday)}
                  onCheckedChange={() => onHolidayChange(holiday)}
                />
                <Label htmlFor={`holiday-${holiday}`} className="text-sm flex-1 flex justify-between">
                  <span>{formatHolidayName(holiday)}</span>
                  <span className="text-muted-foreground ml-2">({(holidayCounts.find(h => h.name === holiday)?.count || 0).toLocaleString()})</span>
                </Label>
              </div>
            ))}
          </div>
        </div>
      </Card>

      {/* Genre Filter */}
      <Card className="p-4">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Label className="text-base font-medium">Genre</Label>
          </div>
          <div className="space-y-2">
            {allGenres.map(genre => (
              <div key={genre} className="flex items-center space-x-2">
                <Checkbox
                  id={`genre-${genre}`}
                  checked={selectedGenres.includes(genre)}
                  onCheckedChange={() => onGenreChange(genre)}
                />
                <Label htmlFor={`genre-${genre}`} className="text-sm">
                  {genre}
                </Label>
              </div>
            ))}
          </div>
        </div>
      </Card>

      {/* Props Used Filter */}
      <PropsUsedFacet className="p-4" />

      {/* Album Filter */}
      <AlbumFacet
        albums={albums}
        selectedAlbums={selectedAlbums}
        onAlbumChange={onAlbumChange}
      />

      {/* Price Range */}
      <Card className="p-4">
        <PriceSlider
          minPrice={0}
          maxPrice={100}
          value={priceRange}
          onValueChange={onPriceChange}
        />
      </Card>
    </div>
  );
} 